/**
 * Prediction Auth - Privy
 */

import Privy, { LocalStorage } from '@privy-io/js-sdk-core';

const PRIVY_APP_ID = import.meta.env.VITE_PRIVY_APP_ID;

if (!PRIVY_APP_ID) {
  console.error('Missing VITE_PRIVY_APP_ID in environment');
}

// Initialize Privy client with localStorage
const privy = new Privy({
  appId: PRIVY_APP_ID,
  storage: new LocalStorage(),
});

export interface AuthUser {
  id: string;
  email?: string;
  wallet?: string;
}

/**
 * Get current user (null if not logged in)
 */
export async function getUser(): Promise<AuthUser | null> {
  try {
    const result = await privy.user.get();
    if (!result?.user) return null;
    
    const user = result.user;
    const emailAccount = user.linked_accounts?.find(a => a.type === 'email');
    const walletAccount = user.linked_accounts?.find(a => a.type === 'wallet');
    
    return {
      id: user.id,
      email: emailAccount && 'address' in emailAccount ? emailAccount.address : undefined,
      wallet: walletAccount && 'address' in walletAccount ? walletAccount.address : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Login with email (sends OTP)
 */
export async function loginWithEmail(email: string): Promise<void> {
  await privy.auth.email.sendCode(email);
}

/**
 * Verify email OTP
 */
export async function verifyEmailCode(email: string, code: string): Promise<AuthUser> {
  await privy.auth.email.loginWithCode(email, code);
  const user = await getUser();
  if (!user) throw new Error('Login failed');
  return user;
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  await privy.auth.logout();
}

/**
 * Get auth token for API calls
 */
export async function getAuthToken(): Promise<string | null> {
  try {
    const result = await privy.getAccessToken();
    if (!result) {
      console.warn('[getAuthToken] No token returned from Privy');
    }
    return result || null;
  } catch (e) {
    console.error('[getAuthToken] Error getting token:', e);
    return null;
  }
}

export interface PredictionData {
  price: number;
  percentChange: number;
  tier: string;
  targetDate?: string;
  // Accuracy fields (only present after settlement)
  settled?: boolean;
  correct?: boolean;
  isWinner?: boolean;
  actualClose?: number;
  priceDiff?: number;
  percentOff?: number;
}

export interface UserStats {
  totalPredictions: number;
  correctPredictions: number;
  wins: number;
  currentStreak: number;
  longestStreak: number;
  score: number;
  settledPredictions: number;
  avgPercentOff: number | null;
}

export interface UserProfile {
  wallet: string | null;
  prediction: PredictionData | null;
  stats: UserStats | null;
}

/**
 * Fetch user profile from backend (includes wallet, prediction, and stats)
 */
export async function fetchUserProfile(): Promise<UserProfile | null> {
  const token = await getAuthToken();
  if (!token) return null;

  try {
    const res = await fetch('/api/predictions/me', {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    
    let prediction: PredictionData | null = null;
    if (data.prediction) {
      const p = typeof data.prediction === 'string' ? JSON.parse(data.prediction) : data.prediction;
      prediction = {
        price: p.price,
        percentChange: p.percentChange,
        tier: p.tier,
        targetDate: p.targetDate,
        // Include accuracy fields if settled
        settled: p.settled,
        correct: p.correct,
        isWinner: p.isWinner,
        actualClose: p.actualClose,
        priceDiff: p.priceDiff,
        percentOff: p.percentOff,
      };
    }
    
    return { 
      wallet: data.wallet || null,
      prediction,
      stats: data.stats || null,
    };
  } catch {
    return null;
  }
}

/**
 * Save wallet to backend
 */
export async function saveWalletToBackend(wallet: string): Promise<string | true> {
  const token = await getAuthToken();
  if (!token) {
    return 'Not logged in. Please log in first.';
  }

  try {
    const res = await fetch('/api/predictions/me', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ wallet }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return err.error || `Server error (${res.status})`;
    }
    return true;
  } catch (e) {
    return `Network error: ${e instanceof Error ? e.message : 'unknown'}`;
  }
}
